import axios from 'axios';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from './firebase';
import { endpoints } from './endpoints';
import { httpClient } from './http-client';
import { fetchMe, type Me } from './me';

/** POST /admin/auth/login response (BE LoginResponse). `sessionToken` is a Firebase CUSTOM token. */
export interface LoginResult {
  sessionToken: string;
  role: string | null;
  mustChangePassword: boolean;
}

/** Wire-level error envelope from the BE (ApiError). */
interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string>;
  meta?: Record<string, unknown>;
}

/**
 * Typed login error — exposes the BE `code` (LOGIN_BAD_CREDENTIALS / ACCOUNT_LOCKED / ACCOUNT_INACTIVE /
 * ACCOUNT_WITHDRAWN (SHCT-689, PRD 21 §7.2 — 탈퇴 is NOT 비활성) /
 * LOGIN_RATE_LIMITED / …) + the optional N/5 count in `meta` so the page can localize via the `ko` bundle
 * (NFR-LOC: the Korean label is the FE's; the count is data).
 */
export class LoginError extends Error {
  constructor(
    public readonly code: string,
    public readonly httpStatus: number,
    public readonly meta?: Record<string, unknown>,
  ) {
    super(code);
  }
}

interface ApiEnvelope<T> {
  data: T;
}

/** Backend-mediated login (FEAT-070, DEC-AUTH-001). Throws {@link LoginError} on a wire-level failure. */
export async function login(email: string, password: string): Promise<LoginResult> {
  try {
    const res = await httpClient.post<ApiEnvelope<LoginResult>>(endpoints.auth.login, { email, password });
    // Contract is `{ data: LoginResult }`. A null body (wrong envelope, SPA
    // fallback HTML, …) would crash the caller destructuring `sessionToken`;
    // surface it as a wire-level failure instead.
    if (res.data?.data == null) {
      throw new LoginError('NETWORK', res.status);
    }
    return res.data.data;
  } catch (err) {
    if (err instanceof LoginError) throw err;
    if (axios.isAxiosError(err) && err.response?.data?.error) {
      const body = err.response.data.error as ApiErrorBody;
      throw new LoginError(body.code, err.response.status, body.meta);
    }
    // Network / unknown — fall through with a sentinel so the page can render the generic fallback.
    throw new LoginError('NETWORK', 0);
  }
}

/** Result of a full login: the profile + permission set from GET /me, plus the forced-reset flag. */
export interface AuthenticateResult {
  me: Me;
  mustChangePassword: boolean;
}

/**
 * Full backend-mediated login (FEAT-001 / FEAT-070): POST credentials, exchange the returned Firebase
 * custom token via signInWithCustomToken (the single-use token is then discarded), and load the
 * profile + permission map from GET /me. Every later request carries the auto-refreshing Firebase ID
 * token (see interceptors). Throws {@link LoginError} on a login-step failure.
 */
export async function authenticate(email: string, password: string): Promise<AuthenticateResult> {
  const { sessionToken, mustChangePassword } = await login(email, password);
  await signInWithCustomToken(auth, sessionToken);
  const me = await fetchMe();
  return { me, mustChangePassword };
}

/**
 * Reload session re-establishment (FEATURE_005 (Login Session Policy), 04_FE B.3). The session/ID token lives
 * in memory only, so a hard reload drops it. This silently re-establishes the session from the HttpOnly
 * refresh cookie (sent automatically; `withCredentials`) plus a custom CSRF header — no token ever touches
 * `localStorage`/`sessionStorage`. The backend rotates the cookie and returns a fresh Firebase custom token,
 * which we exchange exactly like login. A missing / expired / superseded cookie → 401 → `null` (not signed in).
 */
export async function refreshSession(): Promise<AuthenticateResult | null> {
  try {
    const res = await httpClient.post<ApiEnvelope<LoginResult>>(endpoints.auth.refresh, null, {
      headers: { 'X-Refresh-Auth': '1' },
    });
    // A null/undefined body can't re-establish a session; fall through to the
    // catch (treated as signed out) rather than crash on destructure.
    if (res.data?.data == null) return null;
    const { sessionToken, mustChangePassword } = res.data.data;
    await signInWithCustomToken(auth, sessionToken);
    const me = await fetchMe();
    return { me, mustChangePassword };
  } catch {
    // No / expired / superseded refresh cookie (401), or the backend is unreachable — treat as signed out.
    return null;
  }
}

let bootPromise: Promise<AuthenticateResult | null> | null = null;

/**
 * Run the reload re-establish exactly ONCE per page load. The refresh token is single-use (rotated on every
 * call), so React StrictMode's double-effect must not fire two `/auth/refresh` calls — the second would find
 * the token already rotated and 401. Memoizing the promise makes both effects await the same single call.
 */
export function bootstrapSession(): Promise<AuthenticateResult | null> {
  if (!bootPromise) {
    bootPromise = refreshSession();
  }
  return bootPromise;
}

/**
 * Why a logout happened, so the server's audit log can tell an idle-session-timeout logout apart from a
 * user-initiated one (FEATURE_005 / FEATURE_050). The 60-minute idle timer lives here on the front-end, so the
 * front-end is the only party that knows the reason — it tells the server via the `reason` query parameter.
 * `USER` is the default; only the idle timer passes `SESSION_TIMEOUT`.
 */
export type LogoutReason = 'USER' | 'SESSION_TIMEOUT';

/**
 * Logout: drop the server-side refresh token + clear its cookie so a later reload can't re-establish.
 *
 * Best-effort by default (forced 401 / idle-timeout logout always clears locally regardless of the
 * server result). Pass `{ throwOnError: true }` for user-initiated logout, where a failed call must
 * surface to the caller so it can show the failure popup and KEEP the session (SHCT-331 / PRD 20 §3.5).
 *
 * Pass `{ reason: 'SESSION_TIMEOUT' }` from the 60-minute idle timer so the server records the audit row as a
 * session-timeout logout rather than an ordinary user logout (SHCT-148). Omitting it defaults to `USER`.
 */
export async function logoutSession(
  options?: { throwOnError?: boolean; reason?: LogoutReason },
): Promise<void> {
  try {
    // Only send the query param for a non-default reason, so an ordinary user logout stays a plain call.
    const url =
      options?.reason && options.reason !== 'USER'
        ? `${endpoints.auth.logout}?reason=${options.reason}`
        : endpoints.auth.logout;
    await httpClient.post(url, null, { headers: { 'X-Refresh-Auth': '1' } });
  } catch (err) {
    // Non-fatal by default — the cookie is HttpOnly + path-scoped; the in-memory token is already being dropped.
    if (options?.throwOnError) throw err;
  }
}
